import { useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppLayout from "./AppLayout";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Separator } from "./ui/separator";
import { Textarea } from "./ui/textarea";
import { ArrowLeft } from "lucide-react";
import SignatureCanvas from "react-signature-canvas";
import authService from "../Services/authService";

const SampleCollectionScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const sigCanvasRef = useRef();

  const [tests, setTests] = useState([
    { id: 1, name: "Complete Blood Count", collected: false },
    { id: 2, name: "Lipid Profile", collected: false },
    { id: 3, name: "Liver Function Test", collected: false },
  ]);
  const [notes, setNotes] = useState("");
  const [signatureCaptured, setSignatureCaptured] = useState(false);
  const [signatureURL, setSignatureURL] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);



  const toggleTest = (testId) => {
    setTests(tests.map((test) => (test.id === testId ? { ...test, collected: !test.collected } : test)));
  };

  const handleSaveSignature = () => {
    if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) return;

    let dataURL = "";
    try {
      dataURL = sigCanvasRef.current.getTrimmedCanvas().toDataURL("image/png");
    } catch (error) {
      console.warn("getTrimmedCanvas failed, falling back to getCanvas:", error);
      dataURL = sigCanvasRef.current.getCanvas().toDataURL("image/png");
    }

    setSignatureURL(dataURL);
    setSignatureCaptured(true);
  };


  const handleClearSignature = () => {
    sigCanvasRef.current.clear();
    setSignatureCaptured(false);
    setSignatureURL("");
  };

  const handleSubmit = () => {
    if (!signatureCaptured || !signatureURL) return;

    setIsSubmitting(true);

    const token = authService.getCurrentUsertoken();

    fetch(authService.getApiUrl() + 'ordersupdate', {
      method: 'PUT',
      body: JSON.stringify({
        order_id: id,
        collection_notes: notes,
        capture_signature: signatureURL, // This is base64 image string
      }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=UTF-8',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (res.status === 401) {
          authService.clientlogout();
          window.location.href = "/login";
        } else {
          return res.json();
        }
      })
      .then((result) => {
        navigate(`/appointments/${id}/success`);
      })
      .catch((err) => {
        console.error("Error submitting collection:", err.message);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };


  return (
    <AppLayout>
      <div className="flex items-center mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="mr-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="sr-only">Back</span>
        </Button>
        <h1 className="text-xl font-bold text-gray-800">Sample Collection</h1>
      </div>

      <Card className="mb-6 border-gray-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-gray-800 text-left">Collection Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="Add any notes about the collection process..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="min-h-[100px] border-gray-200 text-gray-800 placeholder:text-gray-400"
          />
        </CardContent>
      </Card>

      <Card className="mb-6 border-gray-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-gray-800 text-left">Patient Signature</CardTitle>
        </CardHeader>
        <CardContent>
          {signatureCaptured && signatureURL ? (
            <div className="flex flex-col items-center">
              <div className="relative w-full h-32 bg-gray-100 rounded-md overflow-hidden mb-2">
                <img src={signatureURL} alt="Patient Signature" className="w-full h-full object-contain" />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearSignature}
                className="border-gray-200 hover:bg-gray-50 text-gray-800"
              >
                Clear Signature
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <div className="w-full h-32 bg-gray-50 border border-dashed border-gray-300 rounded-md overflow-hidden mb-2">
                <SignatureCanvas
                  ref={sigCanvasRef}
                  penColor="black"
                  canvasProps={{ width: 500, height: 128, className: "w-full h-full" }}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearSignature}
                  className="border-gray-200 hover:bg-gray-50 text-gray-800"
                >
                  Clear
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSaveSignature}
                  className="border-gray-200 hover:bg-gray-50 text-gray-800"
                >
                  Save Signature
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Button
        className="w-full h-12 text-base bg-pranaam-red hover:bg-red-700 text-white"
        disabled={!signatureCaptured || isSubmitting}
        onClick={handleSubmit}
      >
        {isSubmitting ? "Processing..." : "Mark as Collected"}
      </Button>
    </AppLayout>
  );
};

export default SampleCollectionScreen;
